import './publicPath';
import { clearAllBodyScrollLocks } from '../utils/scroll-lock';

import { wrapTable, wrapIframe } from '../utils/theme-rte.js';
import '../utils/klarnaConfig.js'; // Initialize Klarna configuration
import { initVideoCoordinator } from '../utils/videoCoordinator.js';
import { initScrollPauseVideos } from '../utils/scrollPauseVideos.js';
import { applyVideoFetchPrefix } from '../utils/videoUrlPrefix.js';

// Expose to Constructor's inline JS (Liquid-rendered, not part of the bundle)
// so it can share the same per-placement + DPR-aware Cloudinary URL builder.
if (typeof window !== 'undefined') {
  window.applyVideoFetchPrefix = applyVideoFetchPrefix;
}

import sections from '../utils/theme-sections';

import subNav from '../components/subnav';
import AdMindPageLoad from '../components/AdMind/pageLoad';

// *** Auto-generated imports will be placed below. DO NOT EDIT THIS LINE! ***
// GTM Events
import GTMGlobal from '../components/GTMEvents/index';

// CORE
import locales_by_country from '../utils/locales_by_country';
import formatFreeShippingThresholds from '../utils/formatFreeShippingThreshold';

window.theme = window.theme || {};

window.formatter = new window.Intl.NumberFormat(`${locales_by_country(window.theme.country_iso)}`, {
  style: 'currency',
  currency: window.theme.currency_iso,
  numberingSystem: 'latn',
});

window.formatFreeShippingThresholds = formatFreeShippingThresholds;

window.clearAllBodyScrollLocks = clearAllBodyScrollLocks;

window.addEventListener('DOMContentLoaded', () => {
  let urlWithParams = new URL(window.location.href);

  if (urlWithParams) {
    let turboLinkParam = urlWithParams.searchParams.get('tl');
    if (turboLinkParam) {
      urlWithParams.searchParams.delete('tl');
      history.replaceState({}, '', urlWithParams.href);
    }
  }

  // Pause other managed autoplay videos whenever one starts playing.
  initVideoCoordinator();

  // Pause any video that scrolls out of the viewport (all scenarios).
  initScrollPauseVideos();

  if (document.querySelector('[data-section-type="product"]')) {
    import(/* webpackChunkName: "product-section" */ '../sections/product')
      .then(() => sections.load('product'))
      .catch((err) => console.error('[product section] chunk load failed:', err));
  }

  /* Frame Blocks — lazy-loaded, only on pages that render them */
  function loadFrameBlocks() {
    if (document.querySelector('[data-product-tiles]')) {
      import(/* webpackChunkName: "frame-blocks" */ '../blocks/ProductTiles/index.js')
        .then((m) => m.default())
        .catch((err) => console.error('[ProductTiles] chunk load failed:', err));
    }
    if (document.querySelector('[data-carousel]')) {
      import(/* webpackChunkName: "frame-blocks" */ '../blocks/Carousel/index.js')
        .then((m) => m.default())
        .catch((err) => console.error('[Carousel] chunk load failed:', err));
    }
    if (document.querySelector('.action-video')) {
      import(/* webpackChunkName: "frame-blocks" */ '../blocks/video')
        .then((m) => m.default())
        .catch((err) => console.error('[blockVideo] chunk load failed:', err));
    }
  }
  loadFrameBlocks();

  // These interaction-heavy modules are not needed to paint the first viewport.
  // Keep their listeners available after DOMContentLoaded without parsing them in
  // the global bundle that every template downloads.
  Promise.all([
    import(/* webpackChunkName: "layout-infinite" */ '../infinite-loader'),
    import(/* webpackChunkName: "layout-header" */ '../components/HeaderNav'),
    import(/* webpackChunkName: "layout-menus" */ '../components/navMenu'),
    import(/* webpackChunkName: "layout-forms" */ '../components/RegisterForm'),
    import(/* webpackChunkName: "layout-preheader" */ '../components/PreheaderDropdown'),
    import(/* webpackChunkName: "layout-shipping" */ '../components/ShippingStatus'),
    import(/* webpackChunkName: "layout-cart" */ '../sections/Cart'),
    import(/* webpackChunkName: "layout-search-toggle" */ '../components/QuickSearch/QuickSearchDrawer'),
  ])
    .then(
      ([
        Infinite,
        { default: HeaderNav },
        { default: navMenu },
        { default: RegisterForm },
        { default: PreheaderDropdown },
        { default: ShippingStatus },
        { default: Cart },
        { default: QuickSearchDrawerToggleLogic },
      ]) => {
        Object.values(Infinite).forEach((component) => component());
        subNav();
        HeaderNav();
        navMenu();
        RegisterForm();
        PreheaderDropdown();
        ShippingStatus();
        Cart();
        QuickSearchDrawerToggleLogic();
      },
    )
    .catch((err) => console.error('[layout interactions] chunk load failed:', err));

  if (document.querySelector('[data-bg-src]')) {
    import(/* webpackChunkName: "lazy-bg-image" */ '../utils/lazyBgImage').catch((err) =>
      console.error('[lazyBgImage] chunk load failed:', err),
    );
  }

  // *** Auto-generated imports will be executed below. DO NOT EDIT THIS LINE! ***

  if (document.querySelector('.recently-viewed-l')) {
    import(/* webpackChunkName: "recently-viewed" */ '../sections/RecentlyViewed')
      .then(({ default: RecentlyViewed }) => {
        RecentlyViewed();
      })
      .catch((err) => {
        console.error('[RecentlyViewed] chunk load failed:', err);
      });
  }

  // Recommendations are below the fold on home and PDP. Keep their Constructor
  // bundle out of the critical path until a carousel is near the viewport.
  const recommendationSections = document.querySelectorAll('.product-rec-l');
  if (recommendationSections.length) {
    let recommendationsImportPromise = null;
    const loadRecommendations = () => {
      if (!recommendationsImportPromise) {
        recommendationsImportPromise = import(/* webpackChunkName: "recommendations" */ '../sections/Recommendations')
          .then(({ default: Recommendations }) => {
            window.recommendations = new Recommendations();
            window.recommendations.init();
          })
          .catch((err) => {
            console.error('[Recommendations] chunk load failed:', err);
            recommendationsImportPromise = null;
          });
      }
      return recommendationsImportPromise;
    };

    // PLP and the cart refresh enqueue recs before the observer may have fired,
    // so expose the memoized loader for them to await instead of racing it.
    window.loadRecommendations = loadRecommendations;

    if ('IntersectionObserver' in window) {
      const recommendationObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          recommendationObserver.disconnect();
          loadRecommendations();
        },
        { rootMargin: '600px' },
      );
      recommendationSections.forEach((section) => recommendationObserver.observe(section));
    } else {
      window.setTimeout(loadRecommendations, 1500);
    }
  }

  // simplebar is effectively blog-only: the sole live [data-simplebar] mount is
  // BlogMainHeader's nav strip (sections/BlogMainHeader.liquid). It also drags in
  // core-js, resize-observer-polyfill and three lodash.* packages, all of which sat
  // in the global vendors chunk on every page.
  //
  // Nothing in src/ ever sets data-simplebar - every mount point is server-rendered
  // Liquid - so this single query is a complete test. simplebar's auto-init queries
  // [data-simplebar] at call time and installs a global MutationObserver, so importing
  // it here still initializes elements already in the DOM.
  //
  // Kept here rather than in a template entry: the three templates that render
  // BlogMainHeader (blog, page.blog, search.blog) map to three different entries, and
  // blog.json has no template entry at all - theme.js is the only bundle that covers
  // all of them.
  if (document.querySelector('[data-simplebar]')) {
    import(/* webpackChunkName: "blog-scrollbar" */ 'simplebar').catch((err) => {
      console.error('[simplebar] chunk load failed:', err);
    });
  }

  let quickSearchImportPromise = null;
  function loadQuickSearchChunk() {
    if (!quickSearchImportPromise) {
      quickSearchImportPromise = import(/* webpackChunkName: "quick-search" */ '../components/QuickSearch');
    }
    return quickSearchImportPromise;
  }

  document.querySelectorAll('[data-search-toggle]').forEach((el) => {
    el.addEventListener('mouseenter', () => loadQuickSearchChunk(), { once: true });
  });

  document.addEventListener('searchDrawerOpened', function initQuickSearchOnOpen() {
    loadQuickSearchChunk()
      .then(({ default: QuickSearchAutocomplete }) => {
        window.quickSearchAutocomplete = new QuickSearchAutocomplete();
        window.quickSearchAutocomplete.init();
        document.removeEventListener('searchDrawerOpened', initQuickSearchOnOpen);
      })
      .catch((err) => {
        console.error('[QuickSearch] chunk load failed:', err);
        quickSearchImportPromise = null;
      });
  });

  // QuickView attaches its own document-level click listener for
  // [data-quick-view-button] internally, and on touch devices there's no
  // hover to buy load time before the first tap - so this can't be gated on
  // the same interaction it needs to own. Idle-load instead: still pulled out
  // of the initial chunk, but the listener is attached well before any
  // realistic click.
  const idleLoad = window.requestIdleCallback
    ? (cb) => window.requestIdleCallback(cb, { timeout: 2000 })
    : (cb) => window.setTimeout(cb, 1);

  idleLoad(() => {
    import(/* webpackChunkName: "quick-view" */ '../components/QuickView')
      .then(({ default: QuickView }) => {
        QuickView();
      })
      .catch((err) => {
        console.error('[QuickView] chunk load failed:', err);
      });
  });

  // AdMind cart tracking is a passive listener registry keyed off cartWillUpdate/
  // cartDidUpdate, both dispatched synchronously from sections/Cart/refreshCart.js
  // the instant any add/update/remove flow completes its fetch. There is no safe
  // "about to add to cart" interaction to preload on without risking the listener
  // attaching after that first dispatch, so idle-load it instead - it only needs
  // to be attached sometime before the first cart mutation resolves, not eagerly
  // at parse time. Bounded with a 2s timeout since this feeds addToCart tracking:
  // an unbounded requestIdleCallback could be starved past a very fast quick-add.
  idleLoad(() => {
    import(/* webpackChunkName: "admind-cart" */ '../components/AdMind/cart').catch((err) => {
      console.error('[AdMind/cart] chunk load failed:', err);
    });
  });

  // CountrySelector/NewsletterSignUp are footer-only custom elements that do nothing
  // until they're actually connected in the viewport, so defer both behind a single
  // IntersectionObserver on the footer.
  let footerWidgetsImportPromise = null;
  function loadFooterWidgetsChunk() {
    if (!footerWidgetsImportPromise) {
      footerWidgetsImportPromise = Promise.all([
        import(/* webpackChunkName: "footer-widgets" */ '../components/CountrySelector'),
        import(/* webpackChunkName: "footer-widgets" */ '../components/NewsletterSignUp'),
      ]);
    }
    return footerWidgetsImportPromise;
  }

  const footerEl = document.querySelector('.Footer');
  if (footerEl) {
    const footerObserver = new IntersectionObserver(
      (entries, observer) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadFooterWidgetsChunk()
            .then(([{ default: CountrySelector }, { default: NewsletterSignUp }]) => {
              CountrySelector();
              NewsletterSignUp();
            })
            .catch((err) => {
              console.error('[FooterWidgets] chunk load failed:', err);
              footerWidgetsImportPromise = null;
            });
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    footerObserver.observe(footerEl);
  }

  // *** Auto-generated imports will be executed below. DO NOT EDIT THIS LINE! ***
  if (document.querySelector('[data-validate-form], [data-validate-shopify-form]')) {
    import(/* webpackChunkName: "form-validation" */ '../components/Form')
      .then((m) => m.default())
      .catch((err) => console.error('[Form] chunk load failed:', err));
  }
  GTMGlobal();

  // Fire resize and scroll on page load and section load
  window.dispatchEvent(new Event('resize'));
  window.dispatchEvent(new Event('scroll'));

  document.addEventListener('shopify:section:load', () => {
    window.dispatchEvent(new Event('scroll'));
    window.dispatchEvent(new Event('resize'));
  });

  // Target tables to make them scrollable
  const tableSelectors = '.rte table';

  wrapTable({
    tables: document.querySelectorAll(tableSelectors),
    tableWrapperClass: 'rte__table-wrapper',
  });

  // Target iframes to make them responsive
  const iframeSelectors = '.rte iframe[src*="youtube.com/embed"],' + '.rte iframe[src*="player.vimeo"]';

  wrapIframe({
    iframes: document.querySelectorAll(iframeSelectors),
    iframeWrapperClass: 'rte__video-wrapper',
  });

  document.addEventListener('shopify:section:load', (e) => {
    if (e.detail.sectionId === 'INF-ArticleContent' || e.detail.sectionId === 'INF-PageContent') {
      wrapIframe({
        iframes: document.querySelectorAll(iframeSelectors),
        iframeWrapperClass: 'rte__video-wrapper',
      });
    }
  });

  /* AdMind pageload */
  AdMindPageLoad();
});

['shopify:section:load', 'shopify:section:select', 'shopify:section:reorder', 'shopify:block:select'].forEach(
  (event) => {
    document.addEventListener(event, () => {
      if (document.querySelector('[data-product-tiles]')) {
        import(/* webpackChunkName: "frame-blocks" */ '../blocks/ProductTiles/index.js')
          .then((m) => m.default())
          .catch((err) => console.error('[ProductTiles] chunk load failed:', err));
      }
      if (document.querySelector('[data-carousel]')) {
        import(/* webpackChunkName: "frame-blocks" */ '../blocks/Carousel/index.js')
          .then((m) => m.default())
          .catch((err) => console.error('[Carousel] chunk load failed:', err));
      }
      if (document.querySelector('.action-video')) {
        import(/* webpackChunkName: "frame-blocks" */ '../blocks/video')
          .then((m) => m.default())
          .catch((err) => console.error('[blockVideo] chunk load failed:', err));
      }
    });
  },
);
